/**
 * @alias components.core.VibratePlugin
 * @classdesc Provides an interface for requesting haptics on deviced
 *
 * Currently only supports haptics where [navigator.vibrate]{@link https://caniuse.com/?search=navigator.vibrate} is supported
 *
 * ***This is a core jgg component, and so will be available to any game extending {@link AppBase}.***
 *
 * <pre>
 * this.game.vibrate
 * </pre>
 *
 * The default duration and enabled state can be configured within the [getConfig()]{@link getConfigBase} component options
 *
 * <pre>
 * components : {
 *  vibrate : { data : { defaultDuration, enabled } }
 * }
 * </pre>
 *
 * Or by calling the [setEnabled]{@link components.core.VibratePlugin#setEnabled} and [setDefaultDuration]{@link components.core.VibratePlugin#setDefaultDuration} methods
 *
 * <pre>
 * this.game.vibrate.setDefaultDuration(duration);
 * this.game.vibrate.setEnabled(enabled);
 * </pre>
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Global Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.install('key', VibratePlugin, true, 'scenekey' || null,  { defaultDuration, enabled } );
 * </pre>
 *
 */
class VibratePlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);
    this._enabled = true;
    this._defaultDuration = 200;
    this._supported = false;
    // enable vibration support
    if (navigator.vibrate || navigator.webkitVibrate || navigator.mozVibrate || navigator.msVibrate)
      this._supported = true;
  }

  init(config) {
    if (config) {
      this._defaultDuration = config.defaultDuration || this._defaultDuration;
      this._enabled = config.enabled || this._enabled;
    }
  }

  /**
   * If the vibrate functionality is supported by the device
   * @readonly
   * @type bool
   */
  get supported() {
    return this._supported;
  }

  /**
   * The default duration ( ms ) that the vibration last if no duration is supplied
   * @type number
   * @default 200
   */
  set defaultDuration(duration) {
    this.setDefaultDuration(duration);
  }

  get defaultDuration() {
    return this._defaultDuration;
  }

  /**
   * Set the default duration device should vibrate for on trigger request
   * @param {number} duration The default duration in ms
   */
  setDefaultDuration(duration) {
    this._defaultDuration = duration;
  }

  /**
   * If component is enabled or not. WIll not vibrate if set to false
   * @type {bool}
   * @default true
   */
  set enabled(enabled) {
    this.setEnabled(enabled);
  }

  get enabled() {
    return this._enabled;
  }

  /** enable the component */
  enable() {
    this.setEnabled(true);
  }

  /** disable the component */
  disable() {
    this.setEnabled(false);
  }

  /** enable or disable the component */
  setEnabled(value) {
    this._enabled = value;
  }

  /**
   * Trigger tdevice rotation for a defined amount of time
   * Can also pass through an array of durations to trigger a vibrate sequence
   * <pre>
   * // trigger a once shot vibrate with default duration
   * this.game.vibrate.trigger()
   * // trigger a once shot vibrate with defined duration
   * this.game.vibrate.trigger( 500 )
   * // trigger a vibrate pattern that vibrates for 500ms, then wait 100ms and vibrate again for 500ms
   * this.game.vibrate.trigger( [ 500, 100, 500 ] )
   * </pre>
   *@param {number|number[]} [duration] The duration to vibrate for. If no duration suuplied it will use default of 200ms
   */
  trigger(duration) {
    this._vibrate(duration || this._defaultDuratio);
  }

  /**
   * @private
   */
  _vibrate(value) {
    try {
      navigator.vibrate(value || this._defaultDuratio);
    } catch (e) {}
  }
}

export { VibratePlugin };
